import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

// import "../OrderManage.css";
import Sidebar from "../components/Sidebar";
import DataTable from "react-data-table-component";

import {
  Product,
  OrderStatus,
  Order,
  OrderItemWithProduct,
  OrderItem,
} from "../interfaces/orders_manage";

import { useAuth } from "../context/AuthContext";
import { RootState, AppDispatch } from "../state/store";
import {
  fetchOrdersAndProducts,
  updateOrderStatus,
} from "../state/slices/orderSlice";

import Loading from "../components/Loading";
import DeliveryModal from "../admin/payment/DeliveryModal";
import { getDeliveries } from "../state/slices/deliverySlice";


const statuses: OrderStatus[] = [
  "pending",
  "processing",
  "shipped",
  "delivered",
  "canceled",
];

type OrderType = "online" | "offline";
type FilterTab = "all" | OrderType;

const AdminOrders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  /* =====================================================
     REDUX
     ===================================================== */
  const { orders, products, loading } = useSelector(
    (state: RootState) => state.orders
  );

  const deliveries = useSelector(
    (state: RootState) => state.delivery.deliveries
  );
  const deliveryLoading = useSelector(
    (state: RootState) => state.delivery.loading
  );
  const deliveryError = useSelector(
    (state: RootState) => state.delivery.error
  );

  /* =====================================================
     AUTH
     ===================================================== */
  const { currentUser } = useAuth();

  /* =====================================================
     SIDEBAR
     ===================================================== */
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  /* =====================================================
     FILTERS
     ===================================================== */
  const [selectedTab, setSelectedTab] = useState<FilterTab>("all");
  const [searchText, setSearchText] = useState("");

  /* =====================================================
     SELECTED ORDER
     ===================================================== */
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  /* =====================================================
     DELIVERY / TRACKING
     ===================================================== */
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [activeDeliveryOrder, setActiveDeliveryOrder] = useState<Order | null>(
    null
  );

  /* =====================================================
     REFRESH
     ===================================================== */
  const [refreshCounter, setRefreshCounter] = useState(0);

  const handleRefresh = () => {
    setRefreshCounter((prev) => prev + 1);
  };

  /* =====================================================
     FETCH ORDERS
     ===================================================== */
  useEffect(() => {
    if (!currentUser) return;
    dispatch(fetchOrdersAndProducts());
  }, [currentUser, dispatch, refreshCounter]);

  /* =====================================================
     ENRICH ORDERS
     ===================================================== */
  const enrichedOrders = useMemo(() => {
    if (!orders || !products) return [];

    const productMap = new Map<string, Product>(
      products.map((product: Product) => [product.id, product])
    );

    return orders.map((order: Order) => {
      const enrichedItems = order.items.map((item: OrderItem) => {
        const product = productMap.get(item.productId);
        return {
          ...item,
          productName: product?.name || `Unknown (${item.productId})`,
          vendorId: product?.vendorId,
          product: product,
        } as OrderItemWithProduct;
      });

      return {
        ...order,
        items: enrichedItems,
      };
    });
  }, [orders, products]);

  /* =====================================================
     FILTERED ORDERS (ADMIN SEES ALL)
     ===================================================== */
  const filteredOrders = useMemo(() => {
    const searchTerm = (searchText || "").trim().toLowerCase();

    return enrichedOrders.filter((order: Order) => {
      const typeMatch = selectedTab === "all" || order.orderType === selectedTab;

      const searchMatch =
        !searchTerm ||
        (order.id || "").toLowerCase().includes(searchTerm) ||
        (order.userId || "").toLowerCase().includes(searchTerm) ||
        (order.status || "").toLowerCase().includes(searchTerm) ||
        (order.paymentId || "").toLowerCase().includes(searchTerm) ||
        order.totalAmount?.toString().includes(searchTerm) ||
        new Date(order.createdAt).toLocaleDateString().toLowerCase().includes(searchTerm) ||
        order.items.some(
          (item) =>
            (item.productName || "").toLowerCase().includes(searchTerm) ||
            (item.productId || "").toLowerCase().includes(searchTerm) ||
            (item.vendorId || "").toLowerCase().includes(searchTerm)
        );

      return typeMatch && searchMatch;
    });
  }, [enrichedOrders, selectedTab, searchText]);

  /* =====================================================
     SELECTED ORDER
     ===================================================== */
  const selectedOrder = useMemo(() => {
    return filteredOrders.find((order) => order.id === selectedOrderId);
  }, [filteredOrders, selectedOrderId]);

  /* =====================================================
     HELPERS
     ===================================================== */
  const handleStatusChange = (id: string, newStatus: OrderStatus) => {
    dispatch(updateOrderStatus({ id, status: newStatus }));
  };

  const handleTrackOrder = (order: Order) => {
    setActiveDeliveryOrder(order);
    dispatch(getDeliveries(order.id));
    setShowDeliveryModal(true);
  };

  const closeDeliveryModal = () => {
    setShowDeliveryModal(false);
    setActiveDeliveryOrder(null);
  };

  const handleDeliverySuccess = () => {
    setShowDeliveryModal(false);
    setActiveDeliveryOrder(null);
    handleRefresh();
  };

  const handleSelectOrder = (order: Order) => {
    setSelectedOrderId(order.id);
  };

  const getStatusClass = (status: string) => {
    return `order_status_badge ${status}`;
  };

  const formatDate = (date?: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date?: string) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper to format address defensively
  const renderAddress = (order: any) => {
      console.log("ORDER OBJECT:", order);

    const address = order?.shippingAddress || order?.address || order?.addressId;
    if (!address)
      return (
        <span style={{ color: "#aaa", fontStyle: "italic" }}>
          No address provided.
        </span>
      );

    if (typeof address === "string") {
      return <span>{address}</span>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", color: "#444" }}>
        {address.street && <span>{address.street}</span>}
        {address.city && (
          <span>
            {address.city}
            {address.state ? `, ${address.state}` : ""} {address.zipCode}
          </span>
        )}
        {address.country && <span>{address.country}</span>}
        {address.phone && (
          <span style={{ marginTop: "6px", fontWeight: 600 }}>
            📞 {address.phone}
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return <Loading />;
  }

  /* =====================================================
     MAIN TABLE COLUMNS
     ===================================================== */
  const columns = [
    {
      name: "Order",
      selector: (row: Order) => row.id,
      sortable: true,
      grow: 2,
      cell: (row: Order) => {
        const isSelected = selectedOrderId === row.id;
        return (
          <div
            onClick={() => handleSelectOrder(row)}
            style={{
              cursor: "pointer",
              width: "100%",
              padding: "6px 0",
              borderLeft: isSelected
                ? "4px solid #FF7043"
                : "4px solid transparent",
              paddingLeft: "10px",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                color: isSelected ? "#FF5722" : "#333",
                marginBottom: "4px",
              }}
            >
              #{row.id.substring(0, 8)}...
            </div>
            <div style={{ fontSize: "0.78rem", color: "#777" }}>
              {formatDate(row.createdAt)}
            </div>
            <div style={{ fontSize: "0.78rem", color: "#777", marginTop: "3px" }}>
              {row.items.length} {row.items.length === 1 ? "item" : "items"}
            </div>
          </div>
        );
      },
    },
    {
      name: "Customer ID",
      selector: (row: Order) => row.userId,
      sortable: true,
      grow: 1.5,
      cell: (row: Order) => (
        <span
          style={{
            fontSize: "0.85rem",
            color: "#555",
            wordBreak: "break-all",
          }}
        >
          {row.userId.substring(0, 12)}...
        </span>
      ),
    },
    {
      name: "Total Amount",
      selector: (row: Order) => row.totalAmount,
      sortable: true,
      cell: (row: Order) => (
        <strong style={{ color: "#2E7D32" }}>
          ₹{row.totalAmount.toFixed(2)}
        </strong>
      ),
    },
    {
      name: "Status",
      cell: (row: Order) => (
        <span className={getStatusClass(row.status)}>{row.status}</span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      grow: 1.5,
      cell: (row: Order) => (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
            width: "100%",
          }}
        >
          <button
            type="button"
            onClick={() => handleSelectOrder(row)}
            style={{
              border: "1px solid #FF7043",
              background: "white",
              color: "#FF5722",
              borderRadius: "6px",
              padding: "5px 8px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            View Details
          </button>
          {row.orderType === "online" ? (
            <button
              type="button"
              onClick={() => handleTrackOrder(row)}
              style={{
                border: "none",
                background: "transparent",
                color: "#FF5722",
                cursor: "pointer",
                fontWeight: 700,
                textDecoration: "underline",
              }}
            >
              Track Order
            </button>
          ) : (
            <span
              style={{ color: "#999", fontSize: "0.8rem", textAlign: "center" }}
            >
              Offline
            </span>
          )}
        </div>
      ),
    },
  ];

  /* =====================================================
     PRODUCT COLUMNS
     ===================================================== */
  const productColumns = [
    {
      name: "Product",
      selector: (row: OrderItemWithProduct) => row.productName || row.productId,
      grow: 2.5,
      sortable: true,
      cell: (row: OrderItemWithProduct) => (
        <div style={{ padding: "8px 0" }}>
          <div style={{ fontWeight: 700, color: "#333" }}>
            {row.productName || "Unknown Product"}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#999", marginTop: "3px" }}>
            ID: {row.productId.substring(0, 8)}...
          </div>
          {row.vendorId && (
            <div style={{ fontSize: "0.75rem", color: "#1565C0", marginTop: "3px" }}>
              Vendor: {row.vendorId.substring(0, 8)}...
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Qty",
      selector: (row: OrderItem) => row.quantity,
      sortable: true,
      center: true,
      cell: (row: OrderItem) => (
        <span style={{ fontWeight: 700 }}>×{row.quantity}</span>
      ),
    },
    {
      name: "Unit Price",
      selector: (row: OrderItem) => row.price,
      sortable: true,
      right: true,
      cell: (row: OrderItem) => <span>₹{Number(row.price).toFixed(2)}</span>,
    },
    {
      name: "Subtotal",
      selector: (row: OrderItem) => (Number(row.price) || 0) * (Number(row.quantity) || 0),
      sortable: true,
      right: true,
      cell: (row: OrderItem) => (
        <strong>
          ₹{((Number(row.price) || 0) * (Number(row.quantity) || 0)).toFixed(2)}
        </strong>
      ),
    },
  ];

  /* =====================================================
     RENDER
     ===================================================== */
  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f7f8fa" }}>
      <Sidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />

      {showDeliveryModal && activeDeliveryOrder && (
        <DeliveryModal
          order={activeDeliveryOrder}
          isOpen={showDeliveryModal}
          onClose={closeDeliveryModal}
          onSuccess={handleDeliverySuccess}
          deliveries={deliveries}
          loading={deliveryLoading}
          error={deliveryError}
        />
      )}

      <main
        style={{
          flex: 1,
          marginTop: "60px",
          marginLeft: isSidebarOpen ? "250px" : "60px",
          transition: "margin-left 0.3s ease",
          padding: "28px",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 700, color: "#222" }}>
              Admin Order Management
            </h1>
            <p style={{ margin: "6px 0 0", color: "#777", fontSize: "14px" }}>
              Manage all orders across the platform
            </p>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            style={{
              background: "#FF7043",
              border: "none",
              color: "white",
              padding: "10px 18px",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: 600,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          >
            🔄 Refresh
          </button>
        </div>

        {/* Filter Bar */}
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 1px 5px rgba(0,0,0,0.07)",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            {/* TABS */}
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {(["all", "online", "offline"] as FilterTab[]).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setSelectedTab(tab)}
                  style={{
                    border: selectedTab === tab ? "1px solid #FF5722" : "1px solid #ddd",
                    background: selectedTab === tab ? "#FFF1EC" : "white",
                    color: selectedTab === tab ? "#FF5722" : "#555",
                    padding: "9px 16px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  {tab === "all" ? "All Orders" : tab === "online" ? "🌐 Online" : "🏪 Offline"}
                </button>
              ))}
            </div>

            {/* SEARCH */}
            <div style={{ minWidth: "280px", maxWidth: "420px", flex: "1" }}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search Order ID, Customer, Product..."
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "10px 14px",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  outline: "none",
                  fontSize: "14px",
                }}
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "14px",
            marginBottom: "20px",
          }}
        >
          {/* Total Orders */}
          <div style={{ background: "white", borderRadius: "12px", padding: "18px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)" }}>
            <div style={{ color: "#888", fontSize: "13px" }}>Total Orders</div>
            <div style={{ fontSize: "25px", fontWeight: 700, marginTop: "5px", color: "#222" }}>
              {filteredOrders.length}
            </div>
          </div>

          {/* Pending */}
          <div style={{ background: "white", borderRadius: "12px", padding: "18px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)" }}>
            <div style={{ color: "#888", fontSize: "13px" }}>Pending</div>
            <div style={{ fontSize: "25px", fontWeight: 700, marginTop: "5px", color: "#e67e22" }}>
              {filteredOrders.filter((o) => o.status === "pending").length}
            </div>
          </div>

          {/* Processing */}
          <div style={{ background: "white", borderRadius: "12px", padding: "18px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)" }}>
            <div style={{ color: "#888", fontSize: "13px" }}>Processing</div>
            <div style={{ fontSize: "25px", fontWeight: 700, marginTop: "5px", color: "#3498db" }}>
              {filteredOrders.filter((o) => o.status === "processing").length}
            </div>
          </div>

          {/* Shipped */}
          <div style={{ background: "white", borderRadius: "12px", padding: "18px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)" }}>
            <div style={{ color: "#888", fontSize: "13px" }}>Shipped</div>
            <div style={{ fontSize: "25px", fontWeight: 700, marginTop: "5px", color: "#8e44ad" }}>
              {filteredOrders.filter((o) => o.status === "shipped").length}
            </div>
          </div>

          <div style={{ background: "white", borderRadius: "12px", padding: "18px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)" }}>
            <div style={{ color: "#888", fontSize: "13px" }}>Delivered</div>
            <div style={{ fontSize: "25px", fontWeight: 700, marginTop: "5px", color: "#8e44ad" }}>
              {filteredOrders.filter((o) => o.status === "delivered").length}
            </div>
          </div>
        </div>

        {/* Main Grid: List + Detail Split */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: selectedOrder ? "minmax(0, 1.15fr) minmax(400px, 0.85fr)" : "1fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          {/* LEFT: Orders Table */}
          <div style={{ background: "white", borderRadius: "12px", boxShadow: "0 1px 5px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Orders List</h3>
                <span style={{ color: "#888", fontSize: "12px" }}>Select an order to view complete details</span>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredOrders}
              pagination
              highlightOnHover
              striped
              persistTableHead
              noDataComponent={
                <div style={{ padding: "50px 20px", textAlign: "center", color: "#888" }}>
                  <div style={{ fontSize: "38px", marginBottom: "10px" }}>📦</div>
                  <strong>No orders found</strong>
                  <div style={{ marginTop: "5px", fontSize: "13px" }}>Try changing your filter or search.</div>
                </div>
              }
              customStyles={{
                headCells: {
                  style: {
                    backgroundColor: "#FFF8F6",
                    color: "#FF5722",
                    fontWeight: 700,
                    fontSize: "13px",
                    padding: "12px",
                  },
                },
                rows: {
                  style: {
                    minHeight: "72px",
                    fontSize: "13px",
                    borderBottom: "1px solid #f1f1f1",
                  },
                },
                cells: {
                  style: {
                    padding: "10px",
                  },
                },
              }}
            />
          </div>

          {/* RIGHT: Order Details Panel */}
          {selectedOrder && (
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 1px 8px rgba(0,0,0,0.09)",
                overflow: "hidden",
                position: "sticky",
                top: "20px",
              }}
            >
              {/* Header */}
              <div style={{ background: "linear-gradient(135deg, #FF7043, #FF5722)", color: "white", padding: "22px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "15px", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "12px", opacity: 0.85, marginBottom: "5px" }}>ORDER DETAILS</div>
                    <h2 style={{ margin: 0, fontSize: "22px", wordBreak: "break-all" }}>#{selectedOrder.id}</h2>
                    <div style={{ marginTop: "7px", fontSize: "13px", opacity: 0.9 }}>
                      {formatDateTime(selectedOrder.createdAt)}
                    </div>
                  </div>
                  <span style={{ background: "rgba(255,255,255,0.2)", padding: "7px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {selectedOrder.status.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Detail Content */}
              <div style={{ padding: "20px" }}>
                {/* Info Blocks (Customer, Amount, Payment Method, Payment ID) */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "22px" }}>
                  <div style={{ background: "#f8f9fa", padding: "13px", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>CUSTOMER ID</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, wordBreak: "break-all" }}>{selectedOrder.userId}</div>
                  </div>
                  <div style={{ background: "#FFF3E0", padding: "13px", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#E65100", marginBottom: "4px" }}>TOTAL AMOUNT</div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#E65100" }}>₹{selectedOrder.totalAmount.toFixed(2)}</div>
                  </div>
                  <div style={{ background: "#f8f9fa", padding: "13px", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>ORDER TYPE</div>
                    <div style={{ fontSize: "13px", fontWeight: 600 }}>
                      {selectedOrder.orderType === "online" ? "🌐 Online" : "🏪 Offline"}
                    </div>
                  </div>
                  <div style={{ background: "#f8f9fa", padding: "13px", borderRadius: "8px" }}>
                    <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>PAYMENT ID</div>
                    <strong style={{ fontSize: "12px", wordBreak: "break-all" }}>{selectedOrder.paymentId || "N/A"}</strong>
                  </div>
                </div>

                {/* ADDRESS BLOCK (NEW FOR ADMIN) */}
                <div style={{ marginBottom: "22px", background: "#f9fbfd", border: "1px solid #e2e8f0", padding: "16px", borderRadius: "8px" }}>
                  <h3 style={{ margin: "0 0 10px", fontSize: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
                    <i className="fas fa-map-marker-alt" style={{ color: "#FF5722" }}></i> Shipping Address
                  </h3>
                  <div style={{ fontSize: "13px", lineHeight: "1.5" }}>
                    {renderAddress(selectedOrder)}
                  </div>
                </div>

                {/* Products */}
                <div style={{ marginBottom: "22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <h3 style={{ margin: 0, fontSize: "16px" }}>Ordered Items</h3>
                    <span style={{ fontSize: "12px", color: "#888" }}>{selectedOrder.items.length} item(s)</span>
                  </div>

                  <DataTable
                    columns={productColumns}
                    data={selectedOrder.items}
                    dense
                    highlightOnHover
                    noHeader
                    noTableHead
                    customStyles={{
                      rows: { style: { borderBottom: "1px solid #eee", minHeight: "58px" } },
                      cells: { style: { padding: "8px" } },
                    }}
                  />
                </div>

                {/* Delivery / Track */}
                <div style={{ borderTop: "1px solid #eee", paddingTop: "18px", marginBottom: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ margin: 0, fontSize: "16px" }}>Delivery</h3>
                    {selectedOrder.orderType === "online" && (
                      <button
                        type="button"
                        onClick={() => handleTrackOrder(selectedOrder)}
                        style={{ background: "#FF5722", color: "white", border: "none", borderRadius: "7px", padding: "9px 14px", cursor: "pointer", fontWeight: 600 }}
                      >
                        🚚 Track Order
                      </button>
                    )}
                  </div>
                </div>

                {/* Update Status */}
                <div style={{ borderTop: "1px solid #eee", paddingTop: "18px" }}>
                  <h3 style={{ margin: "0 0 10px", fontSize: "16px" }}>Update Order Status</h3>
                  <select
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value as OrderStatus)}
                    style={{ width: "100%", padding: "11px 12px", border: "1px solid #ddd", borderRadius: "8px", background: "white", fontSize: "14px", cursor: "pointer" }}
                  >
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminOrders;